import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import imageCompression from 'browser-image-compression';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import AssistantMessageBubble from './AssistantMessageBubble';
import { extractStructuredText } from '../../lib/ocrExtract';
import {
  assistantApi,
  type AssistantActionResult,
  type AssistantImageAttachment,
  type AssistantPendingAction,
  type AssistantReplyLink,
  type AssistantSessionResponse,
} from '../../api/assistantApi';

/** sessionStorage marker — present while this browser session has used the chat. */
const VISIT_KEY = 'sog-assistant-visit';

interface UiMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  pendingAction?: AssistantPendingAction;
  /** Deep-links into filtered finance pages that back this answer. */
  links?: AssistantReplyLink[];
  /** Pending action has been confirmed/cancelled/edited → lock its buttons. */
  resolved?: boolean;
  /** data-URI of an attached photo, shown as a thumbnail on the user's bubble. */
  imageUrl?: string;
}

/** Compressed photo staged in the composer, plus a data-URI for preview. */
interface StagedImage extends AssistantImageAttachment {
  kind: 'image';
  dataUrl: string;
  /** Browser Tesseract output — lets the backend fingerprint statements/invoices. */
  extractedText?: string;
}

/** Statement/invoice PDF staged in the composer (sent raw, parsed server-side). */
interface StagedPdf {
  kind: 'pdf';
  base64: string;
  name: string;
}

type StagedAttachment = StagedImage | StagedPdf;

const readDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

let messageSeq = 0;
const nextId = () => `m${Date.now().toString(36)}-${messageSeq++}`;

const mapHistory = (data: AssistantSessionResponse): UiMessage[] =>
  (data.history ?? []).map((turn) => ({
    id: nextId(),
    role: turn.role,
    text: (turn.parts ?? []).map((p) => p.text).join(''),
  }));

interface AssistantChatWidgetProps {
  /**
   * Steward login: photo-first web chat. The widget self-gates by probing
   * /session on mount and stays hidden if the assistant isn't available
   * (backend 403s when the vendor's assistant feature is off).
   */
  stewardMode?: boolean;
}

const AssistantChatWidget = ({ stewardMode = false }: AssistantChatWidgetProps) => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedAttachment | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [reading, setReading] = useState(false);
  // Vendors are already feature-gated in Layout, so they're available up front.
  // Stewards must be confirmed via the /session probe below.
  const [available, setAvailable] = useState<boolean>(!stewardMode);

  const sessionLoadedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Marks this browser session as seen: the first open after a fresh visit
  // starts a clean chat instead of rehydrating stale server context.
  const isFreshVisit = () => {
    if (sessionStorage.getItem(VISIT_KEY)) return false;
    sessionStorage.setItem(VISIT_KEY, '1');
    return true;
  };

  // Steward availability probe: only reveal the widget if /session is reachable.
  useEffect(() => {
    if (!stewardMode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await assistantApi.getSession();
        if (cancelled) return;
        sessionLoadedRef.current = true;
        if (isFreshVisit()) {
          void assistantApi.clearSession().catch(() => undefined);
          setMessages([]);
        } else {
          setMessages(mapHistory(data));
        }
        setAvailable(true);
      } catch {
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stewardMode]);

  // Keep the transcript pinned to the latest message.
  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current;
    if (node && typeof node.scrollTo === 'function') {
      node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, open, sending]);

  const loadSession = useCallback(async () => {
    if (sessionLoadedRef.current) return;
    sessionLoadedRef.current = true;
    if (isFreshVisit()) {
      // New browser session: drop server context instead of rehydrating it.
      void assistantApi.clearSession().catch(() => undefined);
      return;
    }
    setLoadingSession(true);
    try {
      const { data } = await assistantApi.getSession();
      setMessages(mapHistory(data));
    } catch {
      // A fresh vendor has no session yet; start empty rather than error out.
      sessionLoadedRef.current = false;
    } finally {
      setLoadingSession(false);
    }
  }, []);

  const handleClear = useCallback(async () => {
    try {
      await assistantApi.clearSession();
      setMessages([]);
      toast.success('Chat cleared');
    } catch {
      toast.error('Could not clear chat');
    }
  }, []);

  const handleOpen = () => {
    setOpen(true);
    void loadSession();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setAttaching(true);
    try {
      const isPdf =
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        // Statements/invoices: sent raw, text-extracted server-side (PDFBox).
        const dataUrl = await readDataUrl(file);
        setStaged({ kind: 'pdf', base64: dataUrl.split(',')[1] ?? '', name: file.name });
        return;
      }
      // Compression doubles as the pre-OCR downscale (maxWidthOrHeight 1200).
      // HEIC and other undecodable formats land in the catch below.
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
        fileType: 'image/webp',
      });
      const dataUrl = await readDataUrl(compressed);
      const base64 = dataUrl.split(',')[1] ?? '';
      // Local OCR so the backend can fingerprint statement/invoice screenshots
      // without an AI vision call. Failure is fine — the vision path takes over.
      // ponytail: skipping the OpenCV preprocess pipeline here — screenshots are
      // born-digital; reuse ocrPreprocess if photo quality turns out to matter.
      setReading(true);
      let extractedText: string | undefined;
      try {
        const ocr = await extractStructuredText(dataUrl);
        extractedText = ocr.rawText?.trim() ? ocr.rawText : undefined;
      } catch {
        extractedText = undefined;
      } finally {
        setReading(false);
      }
      setStaged({
        kind: 'image',
        base64,
        mimeType: compressed.type || 'image/jpeg',
        dataUrl,
        extractedText,
      });
    } catch {
      toast.error('Could not read that file — use a JPG/PNG photo, a screenshot, or a PDF.');
    } finally {
      setAttaching(false);
      setReading(false);
    }
  };

  const sendStaged = (text: string, attachment: StagedAttachment | null) => {
    if (!attachment) return assistantApi.chat(text || undefined);
    if (attachment.kind === 'pdf') {
      return assistantApi.chat(text || undefined, undefined, { pdfBase64: attachment.base64 });
    }
    const image = { base64: attachment.base64, mimeType: attachment.mimeType };
    return attachment.extractedText
      ? assistantApi.chat(text || undefined, image, { clientExtractedText: attachment.extractedText })
      : assistantApi.chat(text || undefined, image);
  };

  const handleSend = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if ((!text && !staged) || sending) return;
    const attachment = staged;
    setInput('');
    setStaged(null);
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'user',
        text: attachment?.kind === 'pdf' && !text ? `📄 ${attachment.name}` : text,
        imageUrl: attachment?.kind === 'image' ? attachment.dataUrl : undefined,
      },
    ]);
    setSending(true);
    try {
      const { data } = await sendStaged(text, attachment);
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'model', text: data.reply, pendingAction: data.pendingAction, links: data.links },
      ]);
    } catch {
      toast.error('Assistant is unavailable right now. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const resolveAction = async (
    messageId: string,
    actionKey: string,
    call: () => Promise<{ data: AssistantActionResult }>,
  ) => {
    if (busyActionKey) return;
    setBusyActionKey(actionKey);
    try {
      const { data } = await call();
      setMessages((prev) => [
        ...prev.map((m) => (m.id === messageId ? { ...m, resolved: true } : m)),
        // An edit can come back with a revised proposal to confirm.
        { id: nextId(), role: 'model', text: data.message, pendingAction: data.pendingAction },
      ]);
    } catch {
      toast.error('Could not complete that action. Please try again.');
    } finally {
      setBusyActionKey(null);
    }
  };

  const panelWidth = isMobile ? '100%' : 380;
  const panelHeight = isMobile ? '100%' : 'min(620px, calc(100vh - 120px))';
  const cardBg = theme.components?.card?.background || '#FFFFFF';
  const border = theme.components?.card?.border || theme.gray?.[200] || '#E5E5E3';
  const composerPlaceholder = stewardMode
    ? 'Attach a photo of a receipt or count sheet…'
    : 'Ask, or attach an invoice or statement…';

  // Stewards stay hidden until the /session probe confirms availability.
  if (!available) return null;

  return (
    <>
      {!open && (
        <Fab
          data-testid="assistant-fab"
          aria-label="Open AI assistant"
          onClick={handleOpen}
          size="medium"
          sx={{
            position: 'fixed',
            right: isMobile ? 16 : 24,
            // Mobile (≤767px): clear the bottom nav (and iOS home indicator)
            // so the launcher never sits over content or the nav bar.
            bottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom, 0px))' : 24,
            // Below MUI modals/drawers (1300) so open dialogs cover the FAB.
            zIndex: 1250,
            backgroundColor: theme.primary,
            color: 'var(--text-inverse)',
            '&:hover': { backgroundColor: theme.primary, filter: 'brightness(0.95)' },
          }}
        >
          <ChatBubbleLeftRightIcon width={26} />
        </Fab>
      )}

      {open && (
        <Box
          data-testid="assistant-panel"
          role="dialog"
          aria-label="AI assistant"
          sx={{
            position: 'fixed',
            right: isMobile ? 0 : 24,
            bottom: isMobile ? 0 : 24,
            width: panelWidth,
            height: panelHeight,
            maxWidth: '100vw',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            backgroundColor: `${cardBg}E6`,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: isMobile ? 'none' : `1px solid ${border}`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              // Fullscreen on mobile sits under the status bar / notch — pad the
              // header past the safe-area inset so the title clears the clock.
              pt: isMobile ? 'calc(12px + env(safe-area-inset-top))' : 1.5,
              borderBottom: `1px solid ${border}`,
              backgroundColor: `${theme.primary}14`,
            }}
          >
            <SparklesIcon width={20} color={theme.primary} />
            <Typography sx={{ fontWeight: 700, color: theme.text, flexGrow: 1 }}>
              Assistant
            </Typography>
            <IconButton
              data-testid="assistant-clear"
              aria-label="Clear chat"
              size="small"
              onClick={() => void handleClear()}
              disabled={messages.length === 0}
              sx={{ color: theme.gray?.[600] }}
            >
              <TrashIcon width={18} />
            </IconButton>
            <IconButton
              data-testid="assistant-close"
              aria-label="Close assistant"
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: theme.gray?.[600] }}
            >
              <XMarkIcon width={20} />
            </IconButton>
          </Box>

          {/* Transcript */}
          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              px: 1.75,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {loadingSession && messages.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} sx={{ color: theme.primary }} />
              </Box>
            )}

            {!loadingSession && messages.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3, px: 1 }}>
                <Box
                  sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: `${theme.primary}1A`,
                  }}
                >
                  <SparklesIcon width={26} color={theme.primary} />
                </Box>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>
                    {stewardMode ? 'Send me a receipt or count sheet' : 'What can I do for you?'}
                  </Typography>
                  <Typography sx={{ color: theme.gray?.[500], fontSize: '0.85rem', mt: 0.5, lineHeight: 1.5 }}>
                    {stewardMode
                      ? 'Snap a photo and I’ll turn it into an entry to confirm.'
                      : 'Drop in any invoice, receipt, or statement and I’ll turn it into an entry for you — or just ask about your business.'}
                  </Typography>
                </Box>

                {/* Primary CTA — tappable, opens the picker */}
                <Box
                  role="button"
                  tabIndex={0}
                  data-testid="assistant-empty-upload"
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    width: '100%', maxWidth: 320, px: 2, py: 1.5,
                    borderRadius: 2.5, cursor: 'pointer',
                    border: `1.5px dashed ${theme.primary}66`,
                    backgroundColor: `${theme.primary}0D`,
                    transition: 'background-color 120ms, border-color 120ms',
                    '&:hover': { backgroundColor: `${theme.primary}1A`, borderColor: theme.primary },
                    '&:focus-visible': { outline: `2px solid ${theme.primary}`, outlineOffset: 2 },
                  }}
                >
                  <PhotoIcon width={22} color={theme.primary} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: theme.text }}>
                      Upload a document
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: theme.gray?.[500] }}>
                      PDF, photo, or screenshot
                    </Typography>
                  </Box>
                </Box>

                {/* Example prompt chips — tappable, send immediately */}
                {!stewardMode && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', maxWidth: 340 }}>
                    {["Today’s sales", 'Unpaid supplier bills', 'This week’s expenses'].map((q) => (
                      <Box
                        key={q}
                        role="button"
                        tabIndex={0}
                        onClick={() => void handleSend(q)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleSend(q); }}
                        sx={{
                          px: 1.5, py: 0.75, borderRadius: 999, cursor: 'pointer',
                          fontSize: '0.8rem', color: theme.text,
                          border: `1px solid ${border}`, backgroundColor: cardBg,
                          transition: 'background-color 120ms',
                          '&:hover': { backgroundColor: `${theme.primary}12` },
                          '&:focus-visible': { outline: `2px solid ${theme.primary}`, outlineOffset: 2 },
                        }}
                      >
                        {q}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {messages.map((m) => (
              <AssistantMessageBubble
                key={m.id}
                role={m.role}
                text={m.text}
                theme={theme}
                imageUrl={m.imageUrl}
                pendingAction={m.pendingAction}
                links={m.links}
                resolved={m.resolved}
                busy={busyActionKey != null && busyActionKey === m.pendingAction?.actionKey}
                onConfirm={
                  m.pendingAction
                    ? () =>
                        resolveAction(m.id, m.pendingAction!.actionKey, () =>
                          assistantApi.confirmAction(m.pendingAction!.actionKey),
                        )
                    : undefined
                }
                onCancel={
                  m.pendingAction
                    ? () =>
                        resolveAction(m.id, m.pendingAction!.actionKey, () =>
                          assistantApi.cancelAction(m.pendingAction!.actionKey),
                        )
                    : undefined
                }
                onEdit={
                  m.pendingAction
                    ? (instruction) =>
                        resolveAction(m.id, m.pendingAction!.actionKey, () =>
                          assistantApi.editAction(m.pendingAction!.actionKey, instruction),
                        )
                    : undefined
                }
              />
            ))}

            {sending && (
              <Box data-testid="assistant-typing" sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
                <CircularProgress size={14} sx={{ color: theme.primary }} />
                <Typography sx={{ color: theme.gray?.[500], fontSize: '0.8rem' }}>
                  Thinking…
                </Typography>
              </Box>
            )}
          </Box>

          {/* Composer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              px: 1.5,
              py: 1.25,
              // Clear the iOS home-indicator so the input isn't half-hidden.
              pb: isMobile ? 'calc(10px + env(safe-area-inset-bottom))' : 1.25,
              borderTop: `1px solid ${border}`,
              backgroundColor: cardBg,
            }}
          >
            {staged && (
              <Box
                data-testid="assistant-attachment-preview"
                sx={{ position: 'relative', alignSelf: 'flex-start' }}
              >
                {staged.kind === 'image' ? (
                  <Box
                    component="img"
                    src={staged.dataUrl}
                    alt="Attached photo preview"
                    sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 2, border: `1px solid ${border}` }}
                  />
                ) : (
                  <Box
                    data-testid="assistant-attachment-pdf"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 2,
                      border: `1px solid ${border}`,
                      fontSize: '0.8rem',
                      color: theme.text,
                      maxWidth: 240,
                    }}
                  >
                    <span aria-hidden>📄</span>
                    <Typography noWrap sx={{ fontSize: '0.8rem' }}>{staged.name}</Typography>
                  </Box>
                )}
                <IconButton
                  data-testid="assistant-attachment-remove"
                  aria-label="Remove attachment"
                  size="small"
                  onClick={() => setStaged(null)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    p: 0.25,
                    backgroundColor: theme.text,
                    color: 'var(--text-inverse)',
                    '&:hover': { backgroundColor: theme.text },
                  }}
                >
                  <XMarkIcon width={14} />
                </IconButton>
              </Box>
            )}
            {reading && (
              <Typography
                data-testid="assistant-reading"
                sx={{ fontSize: '0.75rem', color: theme.gray?.[500] }}
              >
                Reading…
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              {/* capture removed: forcing the camera would block picking PDFs
                  and statement screenshots from the gallery/files app. */}
              <input
                ref={fileInputRef}
                data-testid="assistant-file-input"
                type="file"
                accept="image/*,application/pdf,.pdf"
                hidden
                onChange={handleFileSelected}
              />
              <IconButton
                data-testid="assistant-attach"
                aria-label="Attach photo or PDF"
                onClick={() => fileInputRef.current?.click()}
                disabled={attaching || sending}
                sx={{ color: theme.gray?.[600] }}
              >
                {attaching ? <CircularProgress size={20} sx={{ color: theme.primary }} /> : <PhotoIcon width={22} />}
              </IconButton>
              <TextField
                data-testid="assistant-input"
                placeholder={composerPlaceholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                maxRows={4}
                size="small"
                fullWidth
                inputProps={{ 'aria-label': 'Message assistant' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
              <IconButton
                data-testid="assistant-send"
                aria-label="Send message"
                onClick={() => void handleSend()}
                disabled={sending || (input.trim().length === 0 && !staged)}
                sx={{
                  backgroundColor: theme.primary,
                  color: 'var(--text-inverse)',
                  '&:hover': { backgroundColor: theme.primary, filter: 'brightness(0.95)' },
                  '&.Mui-disabled': { backgroundColor: theme.gray?.[300], color: 'var(--text-inverse)' },
                }}
              >
                <PaperAirplaneIcon width={20} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AssistantChatWidget;
